import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Download, Upload, FileSpreadsheet, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (value === true) return 'da';
  if (value === false) return 'nu';
  return String(value);
};

const DiffList = ({ title, names, tone, testId }) => {
  if (!names?.length) return null;
  return (
    <div data-testid={testId}>
      <p className={`text-sm font-medium ${tone}`}>{title} ({names.length})</p>
      <ul className="mt-1 max-h-36 overflow-y-auto text-xs text-slate-600 space-y-0.5 pl-1">
        {names.map((name) => <li key={name}>• {name}</li>)}
      </ul>
    </div>
  );
};

export const ProductExcelImport = ({ bankName, onImported }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const selectedFile = useRef(null);

  const reset = () => {
    setPreview(null);
    setFileName('');
    selectedFile.current = null;
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const resp = await axios.get(
        `${API_URL}/api/products/excel-template/${encodeURIComponent(bankName)}`,
        { headers: authHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Grila_${bankName.replace(/[^A-Za-z0-9]+/g, '_')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nu am putut genera fișierul');
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    selectedFile.current = file;
    setFileName(file.name);
    setBusy(true);
    setPreview(null);
    try {
      const form = new FormData();
      form.append('bank_name', bankName);
      form.append('file', file);
      const resp = await axios.post(`${API_URL}/api/products/import-excel/preview`, form, {
        headers: authHeaders(),
      });
      setPreview(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fișierul nu a putut fi citit');
      reset();
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    if (!selectedFile.current) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('bank_name', bankName);
      form.append('file', selectedFile.current);
      const resp = await axios.post(`${API_URL}/api/products/import-excel/apply`, form, {
        headers: authHeaders(),
      });
      toast.success(`${bankName}: ${resp.data.created} produse actualizate din Excel`);
      setOpen(false);
      reset();
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Importul a eșuat');
    } finally {
      setBusy(false);
    }
  };

  const diff = preview?.diff;
  const blocked = !preview || preview.errors?.length > 0 || !preview.parsed_count;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 whitespace-nowrap ml-2"
        data-testid={`excel-import-open-${bankName}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >
        <FileSpreadsheet className="h-4 w-4 mr-1.5" />
        Import Excel
      </Button>

      <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) reset(); }}>
        <DialogContent className="max-w-2xl" data-testid="excel-import-dialog">
          <DialogHeader>
            <DialogTitle>Actualizează grila {bankName} din Excel</DialogTitle>
            <DialogDescription>
              Descarcă grila actuală, modific-o în Excel și încarc-o înapoi. Produsele {bankName} sunt
              înlocuite cu rândurile din fișier — celelalte bănci nu sunt atinse.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleDownload}
                disabled={busy}
                data-testid="excel-download-current"
              >
                <Download className="h-4 w-4 mr-2" />
                Descarcă grila actuală
              </Button>
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                data-testid="excel-choose-file"
              >
                {busy && !preview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Alege fișier Excel
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={handleFile}
                data-testid="excel-file-input"
              />
            </div>

            {fileName && <p className="text-xs text-slate-500">Fișier: {fileName}</p>}

            {preview?.errors?.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="excel-errors">
                <p className="flex items-center gap-2 text-sm font-medium text-red-700">
                  <AlertTriangle className="h-4 w-4" /> Fișierul nu poate fi importat
                </p>
                <ul className="mt-1 text-xs text-red-600 space-y-0.5">
                  {preview.errors.map((err) => <li key={err}>• {err}</li>)}
                </ul>
              </div>
            )}

            {preview && !preview.errors?.length && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="excel-preview">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant="outline">{preview.parsed_count} produse în fișier</Badge>
                  <Badge variant="outline">{preview.existing_count} produse acum</Badge>
                  {diff && <Badge variant="outline" className="text-slate-500">{diff.unchanged} neschimbate</Badge>}
                </div>

                {diff && (
                  <div className="grid gap-3 sm:grid-cols-3">
                    <DiffList title="Noi" names={diff.added} tone="text-emerald-700" testId="excel-diff-added" />
                    <DiffList title="Șterse" names={diff.removed} tone="text-red-700" testId="excel-diff-removed" />
                    <DiffList
                      title="Modificate"
                      names={diff.modified.map((m) => m.name)}
                      tone="text-amber-700"
                      testId="excel-diff-modified"
                    />
                  </div>
                )}

                {diff?.modified?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded border border-amber-200 bg-amber-50 p-2">
                    {diff.modified.map((item) => (
                      <div key={item.name} className="text-xs text-amber-900 mb-1">
                        <span className="font-medium">{item.name}</span>
                        <ul className="pl-3">
                          {item.changes.map((change) => (
                            <li key={change.field}>
                              {change.field}: {formatValue(change.from)} → {formatValue(change.to)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {preview.warnings?.length > 0 && (
                  <ul className="text-xs text-amber-700 space-y-0.5" data-testid="excel-warnings">
                    {preview.warnings.map((warn) => <li key={warn}>⚠ {warn}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="excel-import-cancel">
              Anulează
            </Button>
            <Button onClick={handleApply} disabled={blocked || busy} data-testid="excel-import-apply">
              {busy && preview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Aplică importul
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductExcelImport;
